/**
 * Admin Fix Utility
 * Client-side fixes for admin account recognition and navigation issues
 */

import { supabase } from '@/integrations/supabase/client';
import { logger } from '@/services/logger';

// Special admin accounts that should always have admin privileges
const SPECIAL_ADMIN_EMAILS = ['simmakin@gmail.com', 'simmakin@googlemail.com'];
const ADMIN_USER_ID = 'fa0812e4-a398-4933-a2ab-4f859ceafeed';

/**
 * Check if the current user is a special admin
 */
export const isSpecialAdmin = async (): Promise<boolean> => {
  try {
    const { data: userData } = await supabase.auth.getUser();
    
    if (!userData?.user?.email) {
      return false;
    }
    
    return SPECIAL_ADMIN_EMAILS.includes(userData.user.email.toLowerCase()) ||
           userData.user.id === ADMIN_USER_ID;
  } catch (error) {
    logger.error('Error checking special admin status', { error });
    return false;
  }
};

/**
 * Fix admin account profile if needed
 */
export const fixAdminProfile = async (): Promise<boolean> => {
  try {
    const { data: userData } = await supabase.auth.getUser();
    
    if (!userData?.user) {
      return false;
    }
    
    const isAdmin = SPECIAL_ADMIN_EMAILS.includes(userData.user.email?.toLowerCase() || '') ||
                   userData.user.id === ADMIN_USER_ID;
    
    if (!isAdmin) {
      return false;
    }
    
    logger.info('Fixing admin profile for special admin user', { 
      email: userData.user.email,
      userId: userData.user.id 
    });
    
    // Check circuit breaker first
    const { authCircuitBreaker } = await import('./auth-circuit-breaker');
    
    if (!authCircuitBreaker.canUpdateProfile()) {
      logger.info('Profile update blocked by circuit breaker, but admin already recognized');
      return true; // Return true since we know this is admin anyway
    }

    // Update or create admin profile with only essential fields to avoid 400 errors
    const { error } = await supabase
      .from('profiles')
      .upsert({
        id: userData.user.id,
        email: userData.user.email,
        username: userData.user.email === 'simmakin@gmail.com' ? 'Sy' : 'Admin',
        role: 'admin'
      }, {
        onConflict: 'id'
      });
    
    if (error) {
      logger.error('Failed to fix admin profile', { error });
      // Record the error in circuit breaker
      const { authCircuitBreaker } = await import('./auth-circuit-breaker');
      authCircuitBreaker.recordProfileError();
      return false;
    }
    
    logger.info('Admin profile fixed successfully');
    return true;
  } catch (error) {
    logger.error('Exception in fixAdminProfile', { error });
    return false;
  }
};

/**
 * Check subscription status with fallback logic
 */
export const checkSubscriptionWithFallback = async (userId: string): Promise<boolean> => {
  try {
    // First check if this is a special admin
    if (await isSpecialAdmin()) {
      logger.info('Special admin detected, granting subscription access');
      return true;
    }
    
    // Try RPC function first
    try {
      const { data, error } = await supabase
        .rpc('has_active_subscription', { user_uuid: userId });
      
      if (!error) {
        return data === true;
      }
      
      logger.warn('RPC function failed, using fallback', { error: error.message });
    } catch (rpcError) {
      logger.warn('RPC function exception, using fallback', { error: rpcError });
    }
    
    // Fallback to direct table queries
    return await checkSubscriptionFallback(userId);
  } catch (error) {
    logger.error('Exception in checkSubscriptionWithFallback', { error });
    return false;
  }
};

/**
 * Fallback subscription check using direct table queries
 */
const checkSubscriptionFallback = async (userId: string): Promise<boolean> => {
  try {
    // Check unified_subscriptions table first
    const { data: unifiedSub, error: unifiedError } = await supabase
      .from('unified_subscriptions')
      .select('status, current_period_end')
      .eq('user_id', userId)
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle();
    
    if (!unifiedError && unifiedSub) {
      const isActive = unifiedSub.status === 'active' || unifiedSub.status === 'trialing';
      const notExpired = !unifiedSub.current_period_end || new Date(unifiedSub.current_period_end) > new Date();
      return isActive && notExpired;
    }
    
    // Fallback to old subscriptions table
    const { data: oldSub, error: oldError } = await supabase
      .from('subscriptions')
      .select('status, current_period_end')
      .eq('user_id', userId)
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle();
    
    if (!oldError && oldSub) {
      const isActive = ['active', 'trialing', 'trial'].includes(oldSub.status);
      const notExpired = !oldSub.current_period_end || new Date(oldSub.current_period_end) > new Date();
      return isActive && notExpired;
    }
    
    // Final fallback to profiles table
    const { data: profile, error: profileError } = await supabase
      .from('profiles')
      .select('subscription_status, subscription_end_date')
      .eq('id', userId)
      .single();
    
    if (!profileError && profile) {
      const isActive = ['active', 'trialing', 'trial'].includes(profile.subscription_status);
      const notExpired = !profile.subscription_end_date || new Date(profile.subscription_end_date) > new Date();
      return isActive && notExpired;
    }
    
    return false;
  } catch (error) {
    logger.error('Exception in checkSubscriptionFallback', { error });
    return false;
  }
};

/**
 * Initialize admin fixes on app startup
 */
export const initializeAdminFixes = async (): Promise<void> => {
  try {
    logger.info('Initializing admin fixes...');
    
    // Check if current user is special admin and fix profile if needed
    if (await isSpecialAdmin()) {
      await fixAdminProfile();
    }
    
    logger.info('Admin fixes initialization completed');
  } catch (error) {
    logger.error('Error initializing admin fixes', { error });
  }
};

/**
 * Force refresh user session and profile
 */
export const forceRefreshUserSession = async (): Promise<boolean> => {
  try {
    logger.info('Force refreshing user session...');
    
    // Refresh the session
    const { data, error } = await supabase.auth.refreshSession();
    
    if (error) {
      logger.error('Failed to refresh session', { error });
      return false;
    }
    
    // If special admin, fix profile
    if (await isSpecialAdmin()) {
      await fixAdminProfile();
    }
    
    logger.info('User session refreshed successfully');
    return true;
  } catch (error) {
    logger.error('Exception in forceRefreshUserSession', { error });
    return false;
  }
};

// Export for console debugging
if (typeof window !== 'undefined') {
  (window as any).adminFix = {
    isSpecialAdmin,
    fixAdminProfile,
    checkSubscriptionWithFallback,
    initializeAdminFixes,
    forceRefreshUserSession
  };
} 